import { FormEvent, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Typography,
  InputAdornment,
  Link,
} from "@mui/material";
import Checkbox from "../../components/ui/Checkbox";
import IconButton from "../../components/ui/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoadingButton from "../../components/ui/LoadingButton";
import Input from "../../components/ui/Input";
import { AuthContext } from "../../contexts/AuthContext";
import { useIntl } from "../../contexts/IntlContext";
import { LayoutUnauthenticated } from "../../components/LayoutUnauthenticated";
import api from "../../services/api";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({ email: "", password: "" });

  const { setToken, authInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    if (authInfo) {
      navigate("/");
    }
  }, [authInfo, navigate]);

  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe");
    const savedEmail = localStorage.getItem("email");
    
    if (savedRememberMe === "true" && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    } else {
      setEmail("");
      setRememberMe(false);
    }
  }, []);

  const validateForm = () => {
    let isValid = true;
    const errors = { email: "", password: "" };

    if (!password.trim()) {
      errors.password = intl.formatMessage({ id: 'login.passwordRequired' });
      isValid = false;
    }

    if (!email.trim()) {
      errors.email = intl.formatMessage({ id: 'login.emailRequired' });
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = intl.formatMessage({ id: 'validation.invalidEmail' });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("app/auth/signin", {
        email,
        password,
      });

      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("email", email);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("email");
      }

      setToken(response.data.token);
      toast.success(intl.formatMessage({ id: "login.success" }));
      navigate("/");
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 401) {
        toast.error(intl.formatMessage({ id: 'login.invalidCredentials' }));
      } else if (status === 429) {
        toast.error(intl.formatMessage({ id: 'login.tooManyAttempts' }));
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'messages.connectionError' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutUnauthenticated
      title={intl.formatMessage({ id: 'login.welcome' })}
      subtitle={intl.formatMessage({ id: 'login.adminPanel' })}
    >
      <Typography variant="h6">
        {intl.formatMessage({ id: 'login.enterPanel' })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        {intl.formatMessage({ id: 'login.subtitle' })}
      </Typography>

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Input
          label={intl.formatMessage({ id: 'common.email' })}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={formErrors.email !== ""}
          helperText={formErrors.email}
          slotProps={{
            formHelperText: { sx: { minHeight: '1.5em' } },
          }}
          sx={{ width: "100%", mb: 2 }}
        />

        <Input
          label={intl.formatMessage({ id: 'common.password' })}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={formErrors.password !== ""}
          helperText={formErrors.password}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tooltip={intl.formatMessage({ id: 'login.togglePasswordVisibility' })}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
              ),
            },
            formHelperText: { sx: { minHeight: '1.5em' } },
          }}
          sx={{ width: "100%", mb: 2 }}
        />

        <Checkbox
          label={intl.formatMessage({ id: 'login.rememberMe' })}
          sx={{ mb: 2, "& .MuiFormControlLabel-label": { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          checked={rememberMe}
          onChange={(_event, checked) => setRememberMe(checked)}
        />

        <LoadingButton
          type="submit"
          color="primary"
          loading={loading}
          sx={{ width: "100%", mb: 2 }}
        >
          {intl.formatMessage({ id: 'login.signIn' })}
        </LoadingButton>
      </form>

      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={() => navigate("/forgot-password")}
        sx={{ display: "block", width: "100%", textAlign: "center", mb: 1, color: "text.secondary" }}
      >
        {intl.formatMessage({ id: 'login.forgotPassword' })}
      </Link>
    </LayoutUnauthenticated>
  );
}
